import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

import { setStatsError } from '~/familyAccount/redux/familyAccount.slice'
import { useAppDispatch } from '~/redux/hooks'
import ROUTES from '~/shared/constants/routes.const'
import AchievementItem from '~/statistics/components/ProfileCards/AchievementItem'
import TimeStat from '~/statistics/components/ProfileCards/TimeStat'
import useLearningStats from '~/statistics/hooks/useLearningStats'
import { IUser } from '~/types/user-info-types'

export default function LearningList({ user }: { user?: IUser }): React.ReactElement {
  const { t } = useTranslation('statistics')
  const router = useRouter()
  const dispatch = useAppDispatch()

  const { activitiesDone, bestActivityAccuracy, totalTimeSpent, videoWatchDuration, APIExecutionSteps } =
    useLearningStats({ familyMemberId: user?.id })

  const { days: totalTimeDays, hours: totalTimeHrs, minutes: totalTimeMins } = totalTimeSpent
  const { days: videoDurationDays, hours: videoDurationHrs, minutes: videoDurationMins } = videoWatchDuration

  const { isLoading, isFetching, isUninitialized } = APIExecutionSteps
  const showLoader = isLoading || isUninitialized || isFetching

  useEffect(() => {
    if (router.asPath.includes('family') && APIExecutionSteps?.isError) {
      // Safely handle error property if it exists
      const errorData = (APIExecutionSteps as any)?.error?.data?.error
      if (errorData) {
        dispatch(setStatsError(errorData))
        router.push(ROUTES.FAMILY_ADMIN_PANEL)
      }
    }
  }, [APIExecutionSteps, dispatch, router])

  return (
    <div className="space-y-3">
      <AchievementItem
        imgAlt={t('alt_texts.activities_done')}
        imgSrcKey="scoreboard/activities-done.svg"
        isLoading={showLoader}
        result={activitiesDone}
        title={t('activities-done')}
      />
      <AchievementItem
        imgAlt={t('alt_texts.activity_accuracy')}
        imgSrcKey="scoreboard/accuracy.svg"
        isLoading={showLoader}
        result={`${bestActivityAccuracy}%`}
        title={t('activity-accuracy')}
      />
      <AchievementItem
        imgAlt={t('alt_texts.clock')}
        imgSrcKey="scoreboard/clock.svg"
        isLoading={showLoader}
        result={<TimeStat days={totalTimeDays} hours={totalTimeHrs} minutes={totalTimeMins} />}
        title={t('total-time-spent')}
      />
      <AchievementItem
        imgAlt={t('alt_texts.video_camera')}
        imgSrcKey="scoreboard/video.svg"
        isLoading={showLoader}
        result={<TimeStat days={videoDurationDays} hours={videoDurationHrs} minutes={videoDurationMins} />}
        title={t('video-watching-time')}
      />
    </div>
  )
}
